/**
 * Date utility functions for consistent date handling across the application
 */

/**
 * Safely parse a date string in YYYY-MM-DD format to avoid timezone issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object or null if invalid
 */
export function parseDate(dateString: string): Date | null {
	if (!dateString || typeof dateString !== 'string') {
		return null;
	}

	// Trim and validate format
	const trimmed = dateString.trim();
	const parts = trimmed.split('-');
	if (parts.length !== 3) {
		return null;
	}

	const [year, month, day] = parts.map(Number);

	// Validate the parts are valid numbers (the null checks are for the
	// compiler — the length check above guarantees three parts)
	if (
		year == null ||
		month == null ||
		day == null ||
		isNaN(year) ||
		isNaN(month) ||
		isNaN(day)
	) {
		return null;
	}
	if (month < 1 || month > 12) {
		return null;
	}
	if (day < 1 || day > 31) {
		return null;
	}

	// Create date using local timezone (month is 0-based)
	const date = new Date(year, month - 1, day);

	// Verify the date wasn't auto-corrected by JS (e.g., Feb 31 -> Mar 3)
	// This catches invalid dates like Feb 31, Apr 31, Feb 29 in non-leap years
	if (
		date.getFullYear() !== year ||
		date.getMonth() !== month - 1 ||
		date.getDate() !== day
	) {
		return null;
	}

	return date;
}

/**
 * Format a Date object to YYYY-MM-DD string for API usage
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export function formatDateForAPI(date: Date | null | undefined): string {
	if (date == null) {
		return '';
	}
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/**
 * Format a Date object for display
 * @param date - Date object to format
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string for display
 */
export function formatDateForDisplay(
	date: Date | null,
	options: Intl.DateTimeFormatOptions = {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	},
): string {
	if (!date) {
		return '';
	}
	return date.toLocaleDateString('en-US', options);
}

/**
 * Add days to a date
 * @param date - Base date
 * @param days - Number of days to add (can be negative)
 * @returns New Date object
 */
export function addDays(date: Date, days: number): Date {
	const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	result.setDate(result.getDate() + days);
	return result;
}

/**
 * Check if a date string is valid
 * @param dateString - Date string to validate
 * @returns boolean indicating if the date string is valid
 */
export function isValidDateString(dateString: string): boolean {
	const date = parseDate(dateString);
	return date !== null;
}

/**
 * Get the start of a month
 * @param year - Year
 * @param month - Month (1-based, 1 = January)
 * @returns Date object representing the first day of the month
 */
export function getMonthStart(year: number, month: number): Date | null {
	if (month < 1 || month > 12) {
		return null;
	}
	return new Date(year, month - 1, 1);
}

/**
 * Get the end of a month
 * @param year - Year
 * @param month - Month (1-based, 1 = January)
 * @returns Date object representing the last day of the month, or null if invalid
 */
export function getMonthEnd(year: number, month: number): Date | null {
	if (month < 1 || month > 12) {
		return null;
	}
	return new Date(year, month, 0);
}

/**
 * Get a meaningful date group label for activity/history items.
 * Uses weekly groupings beyond "This Week" for better context.
 * @param date - Date to get the group label for
 * @returns Group label string (e.g., "Today", "Yesterday", "This Week", "Mar 10-16")
 */
export function getDateGroupLabel(date: Date): string {
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

	// Check if today
	if (dateDay.getTime() === today.getTime()) {
		return 'Today';
	}

	// Check if yesterday
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	if (dateDay.getTime() === yesterday.getTime()) {
		return 'Yesterday';
	}

	// Calculate week boundaries (week starts on Monday)
	const getWeekStart = (d: Date): Date => {
		const result = new Date(d.getFullYear(), d.getMonth(), d.getDate());
		const dayOfWeek = result.getDay();
		const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
		result.setDate(result.getDate() - diff);

		return result;
	};

	const getWeekEnd = (d: Date): Date => {
		const start = getWeekStart(d);
		const end = new Date(start);
		end.setDate(end.getDate() + 6);

		return end;
	};

	const thisWeekStart = getWeekStart(today);
	const thisWeekEnd = getWeekEnd(today);

	// Check if this week
	if (dateDay >= thisWeekStart && dateDay <= thisWeekEnd) {
		return 'This Week';
	}

	// Check if last week
	const lastWeekStart = new Date(thisWeekStart);
	lastWeekStart.setDate(lastWeekStart.getDate() - 7);
	const lastWeekEnd = new Date(lastWeekStart);
	lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);

	if (dateDay >= lastWeekStart && dateDay <= lastWeekEnd) {
		return 'Last Week';
	}

	// For older dates, show week range (e.g., "Mar 10-16" or "Mar 28 - Apr 3")
	const weekStart = getWeekStart(date);
	const weekEnd = getWeekEnd(date);

	const months = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
	];
	const startMonth = months[weekStart.getMonth()];
	const endMonth = months[weekEnd.getMonth()];
	const startDay = weekStart.getDate();
	const endDay = weekEnd.getDate();

	if (startMonth === endMonth) {
		return `${startMonth} ${startDay}-${endDay}`;
	} else {
		return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
	}
}
